import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../components/PageBlock";
import {Button, CardBody} from "@material-tailwind/react";
import DataTable from "../../components/DataTable/DataTable";
import {API} from "../../types/API";
import {DataTableRequest} from "../../types/Requests/API/DataTableRequest";
import {AxiosResponse} from "axios";
import TD from "../../components/DataTable/TD";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {ErrorHandler} from "../../hooks/errorHandler";
import {useToastContext} from "../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import useComponentVisible from "../../hooks/useComponentVisible";
import BrokerAvatar from "../../components/BrokerAvatar";
import Modal from "../../components/Modal/Modal";
import {UserListResponse} from "../../types/Responses/UsersResponse";
import FilterDropdown from "../../components/FilterDropdown";
import TableSkeleton from "../../components/Skeletons/TableSkeleton";
import {useNavigate, useParams} from "react-router-dom";
import CountryAvatar from "../../components/CountryAvatar";
import {
    Confirmation,
    ConfirmationFiltersResponse,
    ConfirmationsResponse, ConfirmationT
} from "../../types/Responses/ConfirmationsResponse";
import {useUserContext} from "../../types/contexts/UserContext";
import {WithdrawTransaction} from "../../types/Responses/TransactionsResponse";
import TransactionStatus from "../../components/TransactionStatus";
import {useTranslation} from "react-i18next";
import Card from "../../components/Card";
import {setTitle} from "../../hooks/setTitle";
import {APILink} from "../../types/APILink";
import {checkEnabled2FA} from "../../hooks/checkEnabled2FA";
import usePersistedDataTableRequest from "../../hooks/usePersistedDataTableRequest";
import {useDispatch} from "react-redux";
import {setSidebarTitle} from "../../reducers/sidebar";
import FilterChips, {FilterChipDefinition} from "../../components/FilterChips";

type ConfirmationsDataTableRequest = DataTableRequest & { user?: number, status?: number, type?: number };

const Logs = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const toast = useToastContext();
    const navigate = useNavigate();
    const user = useUserContext();
    const dispatch = useDispatch();
    const handleError = useErrorHandler();
    const [users, setUsers] = useState<UserListResponse>();
    const [filterData, setFilterData] = useState<ConfirmationFiltersResponse>();
    const [confirmations, setConfirmations] = useState<ConfirmationsResponse>();
    const [modalConfirmation, setModalConfirmation] = useState<Confirmation>();
    const {ref, isComponentVisible, setIsComponentVisible} = useComponentVisible(false);
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const defaultRequest = useMemo<ConfirmationsDataTableRequest>(() => ({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 1
    }), []);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<ConfirmationsDataTableRequest>(defaultRequest);
    const filterUser = dataTableRequest.user ?? 0;
    const filterStatus = dataTableRequest.status ?? 0;
    const filterType = dataTableRequest.type ?? 0;

    const clearRequest = (request: DataTableRequest | object) => {
        const keys = Object.keys(request);
        if (keys.includes('user') && typeof request['user'] !== "undefined" && parseInt(request['user']) === 0)
            delete request['user'];
        if (keys.includes('status') && typeof request['status'] !== "undefined" && parseInt(request['status']) === 0)
            delete request['status'];
        if (keys.includes('type') && typeof request['type'] !== "undefined" && parseInt(request['type']) === 0)
            delete request['type'];
        return request;
    }

    const openModal = useCallback((confirmation: Confirmation) => {
        setModalConfirmation(confirmation);
        setIsComponentVisible(true);
    }, [setModalConfirmation, setIsComponentVisible]);

    const sendRequest = useCallback((request: object) => {
        setIsDataTableLoading(true);
        API.getConfirmations(clearRequest(request)).then((response: AxiosResponse) => {
            setConfirmations(response.data as ConfirmationsResponse);
            setIsDataTableLoading(false);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_CONFIRMATIONS}`, toast, handleError));
    }, [toast, handleError]);

    const onDataTableChanged = useCallback((request: ConfirmationsDataTableRequest) => {
        const updatedRequest = {
            ...request,
            user: filterUser,
            status: filterStatus,
            type: filterType
        };
        persistDataTableRequest(updatedRequest);
        if (!request.search || request.search.length > 3) {
            sendRequest(!request.search ? {...updatedRequest, search: ''} : updatedRequest);
        }
    }, [filterUser, filterStatus, filterType, sendRequest, persistDataTableRequest]);

    const getUsers = useCallback(() => {
        API.getUsersList(1).then((response: AxiosResponse) => {
            setUsers(response.data as UserListResponse);
        }).catch(e => ErrorHandler(e,`get${APILink.GET_USERS_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const getFilterData = useCallback(() => {
        API.getConfirmationsFilters().then((response: AxiosResponse) => {
            setFilterData(response.data as ConfirmationFiltersResponse);
        }).catch(e => ErrorHandler(e,`get${APILink.GET_CONFIRMATIONS_FILTERS}`, toast, handleError));
    }, [toast, handleError]);

    const onUserChanged = useCallback((u: string | number) => {
        const parsedUser = typeof u === "string" ? parseInt(u) : u;
        const request = {
            ...dataTableRequest,
            page: 1,
            user: parsedUser,
            status: filterStatus,
            type: filterType,
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [dataTableRequest, filterStatus, filterType, sendRequest, persistDataTableRequest]);

    const onStatusChanged = useCallback((u: string | number) => {
        const parsedStatus = typeof u === "string" ? parseInt(u) : u;
        const request = {
            ...dataTableRequest,
            page: 1,
            status: parsedStatus,
            user: filterUser,
            type: filterType,
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [dataTableRequest, filterUser, filterType, sendRequest, persistDataTableRequest]);

    const onTypeChanged = useCallback((u: string | number) => {
        const parsedType = typeof u === "string" ? parseInt(u) : u;
        const request = {
            ...dataTableRequest,
            page: 1,
            status: filterStatus,
            user: filterUser,
            type: parsedType,
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [dataTableRequest, filterUser, filterStatus, sendRequest, persistDataTableRequest]);

    const closeModal = useCallback(() => {
        setIsComponentVisible(false);
    }, [setIsComponentVisible]);

    const modifyConfirmations = useCallback((response: AxiosResponse, confirmations: ConfirmationsResponse, modalConfirmation: Confirmation) => {
        let searchIndex = 0;
        confirmations.data.forEach((c, index) => {
            if (c.id === modalConfirmation.id)
                searchIndex = index;
        })
        const dt = [...confirmations.data];
        dt[searchIndex] = response.data.data;
        setConfirmations({...confirmations, data: dt})
    }, []);

    const decline = useCallback((id: number) => {
        if (!confirmations || !modalConfirmation)
            return;
        API.declineConfirmation(id).then((response: AxiosResponse) => {
            setModalConfirmation(response.data.data);
            modifyConfirmations(response, confirmations, modalConfirmation);
        }).catch(e => ErrorHandler(e,`put${APILink.DECLINE_CONFIRMATION.replace('$confirmation', id.toString())}`,  toast, handleError));
    }, [confirmations, modalConfirmation]);

    const approve = useCallback((id: number) => {
        if (!confirmations || !modalConfirmation)
            return;
        API.approveConfirmation(id).then((response: AxiosResponse) => {
            setModalConfirmation(response.data.data);
            modifyConfirmations(response, confirmations, modalConfirmation);
        }).catch(e => ErrorHandler(e,`put${APILink.APPROVE_CONFIRMATION.replace('$confirmation', id.toString())}`, toast, handleError));
    }, [confirmations, modalConfirmation]);

    useEffect(() => {
        setTitle(t('admin.confirmations.title'));
        dispatch(setSidebarTitle(t('admin.confirmations.title')))
        if (user && user.user){
            checkEnabled2FA(user.user).then(() => {
                const routeUser = id ? parseInt(id) : undefined;
                const request = (!dataTableRequest.user && routeUser)
                    ? {...dataTableRequest, user: routeUser}
                    : dataTableRequest;
                if (!dataTableRequest.user && routeUser) {
                    persistDataTableRequest(request);
                }
                sendRequest(request);
                getUsers();
                getFilterData();
            }).catch()
        }
    }, [user, id]);


    const filterChipsItems = useMemo<FilterChipDefinition[]>(() => {
        const chips: FilterChipDefinition[] = [];
        if (users?.data && filterUser > 0) {
            const selectedUser = users.data.find((u) => parseInt(u.id) === filterUser);
            if (selectedUser) {
                chips.push({
                    key: `user-${selectedUser.id}`,
                    label: `${t('admin.confirmations.table.created_by')}: ${selectedUser.name}`,
                    onRemove: () => onUserChanged('0')
                });
            }
        }
        if (filterData?.data.statuses && filterStatus > 0) {
            const status = filterData.data.statuses.find((s) => s.id === filterStatus);
            if (status) {
                chips.push({
                    key: `status-${status.id}`,
                    label: `${t('admin.confirmations.table.status')}: ${status.name}`,
                    onRemove: () => onStatusChanged('0')
                });
            }
        }
        if (filterData?.data.types && filterType > 0) {
            const type = filterData.data.types.find((tp) => tp.id === filterType);
            if (type) {
                chips.push({
                    key: `type-${type.id}`,
                    label: `${t('admin.confirmations.table.type')}: ${type.name}`,
                    onRemove: () => onTypeChanged('0')
                });
            }
        }
        return chips;
    }, [users, filterUser, filterData, filterStatus, filterType, t, onUserChanged, onStatusChanged, onTypeChanged]);

    const filters = useMemo(() => {
        if (!users || !filterData)
            return <></>;

        return <>
            <FilterDropdown list={users.data.map((u) => {
                return {name: u.name, id: parseInt(u.id)}
            })} name={t('admin.confirmations.table.created_by')} selected={filterUser} className="mb-2 lg:mb-0"
                            onChange={onUserChanged}/>
            <FilterDropdown list={filterData.data.statuses} name={t('admin.confirmations.table.status')} selected={filterStatus}
                            onChange={onStatusChanged} className="mb-2 lg:mb-0"/>
            <FilterDropdown list={filterData.data.types} name={t('admin.confirmations.table.type')} selected={filterType}
                            onChange={onTypeChanged} className="mb-2 lg:mb-0"/>
        </>
    }, [users, filterData, filterUser, filterStatus, filterType, onTypeChanged, onUserChanged, onStatusChanged, t]);

    const modalUI = useMemo(() => {
        if (!modalConfirmation || !isComponentVisible)
            return <></>;

        const withdrawObject = modalConfirmation.object as WithdrawTransaction;

        const changedBy = modalConfirmation.changed_by ? <tr>
            <TD><><span className="font-light">{t('admin.confirmations.table.changed_by')}:</span> <span
                className="cursor-pointer hover:text-teal-500"
                onClick={() => navigate(`/app/admin/users/${modalConfirmation?.changed_by?.id}/profile`)}
            ><BrokerAvatar
                size="xs" image={modalConfirmation.changed_by.image}/><>
                                {modalConfirmation.changed_by.first_name} {modalConfirmation.changed_by.last_name}
                                </></span></>
            </TD>
            <TD><><span className="font-light">{t('admin.confirmations.modified_at')}:</span> {modalConfirmation.updated}</>
            </TD>
        </tr> : '';

        return <Modal refer={ref} showModal={isComponentVisible} title={modalConfirmation.type}>
            <div className="w-full">
                <FontAwesomeIcon icon={solid('xmark')} size="lg"
                                 className="cursor-pointer absolute top-8 right-4 dark:text-white"
                                 onClick={() => closeModal()}/>
                <div className="w-full">
                    {modalConfirmation.type === ConfirmationT.WITHDRAW && withdrawObject ? <div className="w-full">
                        <table className="table w-full">
                            <tbody>
                            <tr>
                                <TD><><span className="font-light">{t('admin.confirmations.table.created_by')}:</span> <span
                                    className="cursor-pointer hover:text-teal-500"
                                    onClick={() => navigate(`/app/admin/users/${modalConfirmation?.created_by.id}/profile`)}><BrokerAvatar
                                    size="xs" image={modalConfirmation.created_by.image}/><>
                                {modalConfirmation.created_by.first_name} {modalConfirmation.created_by.last_name}
                                </></span></>
                                </TD>
                                <TD><><span className="font-light">{t('admin.confirmations.table.date')}:</span> {modalConfirmation.date}</>
                                </TD>
                            </tr>
                            {changedBy}
                            <tr>
                                <TD><><span className="font-light">{t('admin.confirmations.broker')}:</span> <span
                                    className="cursor-pointer hover:text-teal-500"
                                    onClick={() => navigate(`/app/admin/users/${withdrawObject.broker.id}/profile`)}><BrokerAvatar
                                    size="xs" image={withdrawObject.broker.image}/>
                                    {withdrawObject.broker.name}
                                </span></>
                                </TD>
                                <TD>
                                    <><span className="font-light">{t('admin.confirmations.provider')}:</span> {withdrawObject.provider.name}</>
                                </TD>
                            </tr>
                            <tr>
                                <TD>
                                    <><span className="font-light">{t('admin.confirmations.country')}:</span> <CountryAvatar
                                        country={withdrawObject.client.country}/></>
                                </TD>
                                <TD>
                                    <><span
                                        className="font-light">{t('admin.confirmations.amount')}:</span> {withdrawObject.amount} {withdrawObject.currency.code}</>
                                </TD>
                            </tr>
                            <tr>
                                <TD>
                                    <><span className="font-light">{t('admin.confirmations.client_name')}:</span> {withdrawObject.client.name}</>
                                </TD>
                                <TD>
                                    <><span className="font-light">{t('admin.confirmations.client_email')}:</span> {withdrawObject.client.email}</>
                                </TD>
                            </tr>
                            </tbody>
                        </table>
                        {modalConfirmation.payload && Object.keys(modalConfirmation.payload).length > 0 ?
                            <div className="my-8">
                                <div>
                                    <b>{t('admin.confirmations.incoming_request')}</b>
                                </div>
                                <code>
                                    {JSON.stringify(modalConfirmation.payload)}
                                </code>
                            </div> : ''}
                        {modalConfirmation.status.toLowerCase() !== 'pending' ?
                            <div className="text-center my-8">
                                {t('admin.confirmations.table.status')}: <TransactionStatus status={modalConfirmation.status}/>
                            </div> : ''}
                        {modalConfirmation.status.toLowerCase() === 'pending' ? <div className="text-center mt-16">
                            <Button size="sm" className="bg-red-500 text-white mr-8"
                                    onClick={() => decline(modalConfirmation?.id)}>Decline</Button>
                            <Button size="sm" className="bg-teal-500 text-white"
                                    onClick={() => approve(modalConfirmation?.id)}>Approve</Button>
                        </div> : ''}
                    </div> : ''}

                </div>
            </div>
        </Modal>
    }, [modalConfirmation, isComponentVisible, closeModal, ref, approve, decline]);

    const dataTable = useMemo(() => {
        if (!confirmations || !user || !user.user)
            return <TableSkeleton/>;

        return <DataTable {...dataTableRequest}
                          total_pages={confirmations.pages.total}
                          count={confirmations.count}
                          nodata={confirmations.data.length === 0}
                          filters={filters}
                          fullWidthFilters={<FilterChips className="px-4 -mt-6 mb-6" items={filterChipsItems}/>}
                          inProgress={isDataTableLoading}
                          options={{searching: false, length: true, sorting: true}}
                          onChange={onDataTableChanged} headers={[
            {name: '#', sortBy: 'id'},
            {name: t('admin.confirmations.table.created_by'), sortBy: 'created_by'},
            {name: t('admin.confirmations.table.changed_by'), sortBy: 'changed_by'},
            {name: t('admin.confirmations.table.type'), sortBy: 'type_id'},
            {name: t('admin.confirmations.table.status'), sortBy: 'status_id'},
            {name: t('admin.confirmations.table.date'), sortBy: 'created_at'},
            {name: '', className: 'text-right'}
        ]}>
            <tbody>
            {confirmations.data.map((confirmation, index) => {
                    return <tr key={index}>
                        <TD>{((dataTableRequest.page - 1) * dataTableRequest.length) + index + 1}</TD>
                        <TD>
                            <span className="cursor-pointer hover:text-teal-500"
                                  onClick={() => navigate(`/app/admin/users/${confirmation.created_by.id}/profile`)}>
                                <BrokerAvatar image={confirmation.created_by.image}/>
                                {confirmation.created_by.first_name} {confirmation.created_by.last_name}
                            </span>
                        </TD>
                        <TD>{confirmation.changed_by ?
                            <span className="cursor-pointer hover:text-teal-500"
                                  onClick={() => navigate(`/app/admin/users/${confirmation.changed_by?.id}/profile`)}>
                                <BrokerAvatar image={confirmation.changed_by?.image}/>
                                {confirmation.changed_by.first_name} {confirmation.changed_by.last_name}
                            </span>
                            : ''}</TD>
                        <TD>{confirmation.type}</TD>
                        <TD><TransactionStatus status={confirmation.status}/></TD>
                        <TD>{confirmation.date}</TD>
                        <TD className="text-right">
                            {user.user.permissions.includes('change confirmation state') ?
                                <Button onClick={() => openModal(confirmation)}
                                        className="px-5 py-2 text-white bg-black mr-2"><FontAwesomeIcon
                                    icon={solid("eye")}/></Button> : ''}
                        </TD>
                    </tr>
                }
            )}
            </tbody>
        </DataTable>
    }, [dataTableRequest, isDataTableLoading, confirmations, user, filters, filterChipsItems, onDataTableChanged, openModal]);

    return (
        <PageBlock>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {modalUI}
                    {dataTable}
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(Logs);
